/**
 * O quadro: a fábrica inteira em colunas, ao vivo.
 *
 * Feito para ficar num monitor do escritório e ser lido de longe — por isso o
 * modo `tv`, que some com a casca da janela e aumenta a fonte. É o substituto
 * do quadro branco: quem quiser saber onde está o material olha a parede.
 *
 * **Um cartão é uma pilha de material, não uma etapa.** A OS 113903 tem três
 * etapas no acabamento (corte especial, guilhotina, envelopes) e uma pilha só
 * — mostrá-la três vezes na mesma coluna encheria o quadro de repetição e
 * faria parecer que há três trabalhos ali. O cartão mostra a etapa da vez e
 * diz quantas ainda vêm no mesmo setor.
 *
 * A coluna da Expedição não vem de etapa nenhuma: ela é o que já ficou pronto
 * e espera sair.
 */

import { formato, type ItemDaFila } from '@pcp/dominio';
import type { ColunaDoQuadro, LinhaCarteira } from '@pcp/dados';
import {
  cartaoDaFila,
  cartaoOs,
  h,
  render,
  sf,
  situacaoDaEtapa,
  vazio,
  type Filho,
} from '@pcp/ui';
import type { Aplicacao } from '../estado.js';

export async function telaQuadro(
  app: Aplicacao,
  destino: HTMLElement,
  modoTv: boolean,
): Promise<void> {
  const colunas = await app.repo.quadro();
  const carteira = await app.repo.carteira();
  const busca = app.busca.trim().toLowerCase();
  const abrir = (numero: number): void => app.roteador.ir(`/os/${numero}`);

  const combina = (numero: number, cliente: string, produto: string): boolean =>
    !busca ||
    String(numero).includes(busca) ||
    cliente.toLowerCase().includes(busca) ||
    produto.toLowerCase().includes(busca);

  const prontas = carteira.filter(
    (l) => l.estado.status === 'pronta' && combina(l.os.numero, l.os.clienteCurto, l.os.produto),
  );

  const colunasDeProducao = colunas
    .filter((c) => c.setor.papel !== 'expedicao')
    .map((c) => ({
      ...c,
      itens: c.itens.filter((i) => combina(i.os.numero, i.os.clienteCurto, i.os.produto)),
    }));

  const total = colunasDeProducao.reduce((s, c) => s + c.itens.length, 0) + prontas.length;
  const colunaExpedicao = colunas.find((c) => c.setor.papel === 'expedicao');

  render(destino, [
    h(
      `.pcp-quadro${modoTv ? '.pcp-tv' : ''}`,
      { style: { height: '100%' } },
      total === 0
        ? vazio(
            'square-grid-2x2',
            busca ? 'Nada com esse termo no quadro' : 'Nenhuma etapa em fila',
            busca
              ? 'Limpe a busca para ver a fábrica inteira.'
              : 'O quadro mostra o que está esperando ou rodando em cada setor. Importe OS para vê-lo trabalhar.',
          )
        : [
            ...colunasDeProducao.map((c) => colunaDeProducao(c, abrir)),
            colunaExpedicao ? colunaDeExpedicao(colunaExpedicao.setor, prontas, abrir) : null,
          ],
    ),
  ]);
}

/* ------------------------------------------------------------------ */

interface PilhaNoSetor {
  /** A etapa que vem agora neste setor. */
  atual: ItemDaFila;
  /** Quantas outras etapas do mesmo setor esperam depois dela. */
  restantes: number;
}

/** Agrupa por OS: uma pilha de material, um cartão. */
function pilhas(itens: readonly ItemDaFila[]): PilhaNoSetor[] {
  const porOs = new Map<number, ItemDaFila[]>();
  for (const i of itens) {
    const lista = porOs.get(i.os.numero);
    if (lista) lista.push(i);
    else porOs.set(i.os.numero, [i]);
  }

  const saida: PilhaNoSetor[] = [];
  for (const lista of porOs.values()) {
    // A ordem que veio da fila já é a certa; em execução vem antes de fila.
    const atual = lista[0];
    if (atual) saida.push({ atual, restantes: lista.length - 1 });
  }

  const peso = (p: PilhaNoSetor): number =>
    p.atual.estado.status === 'em_execucao' ? 0 : p.atual.estado.status === 'pausada' ? 1 : 2;
  return saida.sort(
    (a, b) =>
      peso(a) - peso(b) ||
      Date.parse(a.atual.os.previsaoEntregaEm) - Date.parse(b.atual.os.previsaoEntregaEm),
  );
}

function colunaDeProducao(c: ColunaDoQuadro, aoAbrir: (numero: number) => void): HTMLElement {
  const grupos = pilhas(c.itens);
  const emExecucao = grupos.filter((p) => p.atual.estado.status === 'em_execucao').length;
  const pausadas = grupos.filter((p) => p.atual.estado.status === 'pausada').length;
  const horas = c.itens.reduce((s, i) => s + (i.etapa.tempoPrevistoSeg ?? 0), 0);

  return h('.pcp-coluna', { style: { '--pcp-cor': c.setor.cor } }, [
    topo(c.setor.nome, grupos.length, emExecucao, pausadas, horas),
    h(
      '.pcp-coluna-lista',
      grupos.length === 0
        ? colunaVazia('Sem fila')
        : grupos.map((p) => cartaoDaPilha(p, aoAbrir)),
    ),
  ]);
}

function cartaoDaPilha(p: PilhaNoSetor, aoAbrir: (numero: number) => void): HTMLElement {
  const cartao = cartaoDaFila(p.atual, aoAbrir);
  if (p.restantes > 0) {
    cartao.querySelector('.pcp-os-rodape')?.append(
      h(
        'span.pcp-marca',
        { title: 'Etapas que ainda vêm neste mesmo setor' },
        [sf('list-bullet'), `+${p.restantes}`],
      ),
    );
  }
  return cartao;
}

function colunaDeExpedicao(
  setor: { nome: string; cor: string },
  prontas: LinhaCarteira[],
  aoAbrir: (numero: number) => void,
): HTMLElement {
  return h('.pcp-coluna', { style: { '--pcp-cor': setor.cor } }, [
    topo(setor.nome, prontas.length, 0, 0, null),
    h(
      '.pcp-coluna-lista',
      prontas.length === 0
        ? colunaVazia('Nada esperando saída')
        : prontas.map((l) =>
            cartaoOs(l, {
              aoClicar: aoAbrir,
              rodapeExtra: h('span.pcp-marca.erp', [
                sf('tray'),
                l.os.entregaTipo === 'RETIRADA' ? 'retirada' : 'entregar',
              ]),
            }),
          ),
    ),
  ]);
}

function topo(
  nome: string,
  quantas: number,
  emExecucao: number,
  pausadas: number,
  horas: number | null,
): Filho {
  return [
    h('.pcp-coluna-topo', [
      h('.titulo', nome),
      h('.spacer'),
      quantas > 0 ? h('span.selo.s-cinza', String(quantas)) : null,
    ]),
    quantas > 0
      ? h('.hstack.gap-2', { style: { padding: '0 8px 4px' } }, [
          emExecucao > 0
            ? h('span.t-subhead', { style: { color: 'var(--pcp-producao)' } }, [
                sf('figure-run', 'sf-xs'),
                ` ${emExecucao} rodando`,
              ])
            : null,
          pausadas > 0
            ? h('span.t-subhead', { style: { color: 'var(--pcp-pausada)' } }, [
                sf('pause-fill', 'sf-xs'),
                ` ${pausadas} parada${pausadas > 1 ? 's' : ''}`,
              ])
            : null,
          h('.spacer'),
          horas !== null
            ? h('span.t-subhead.t-tertiary', { title: 'Tempo previsto na fila deste setor' }, formato.hhmm(horas))
            : null,
        ])
      : null,
  ];
}

function colunaVazia(texto: string): HTMLElement {
  return h('.pcp-coluna-vazia', [sf('checkmark-circle'), h('div', texto)]);
}

/** As ações do quadro, colocadas na barra de ferramentas pela casca. */
export function acoesDoQuadro(app: Aplicacao, modoTv: boolean): Filho {
  return [
    h(
      'button.bt.bt-s.bt-p2',
      {
        type: 'button',
        onclick: () => {
          if (modoTv) {
            document.exitFullscreen?.().catch(() => undefined);
            app.roteador.ir('/quadro');
          } else {
            app.roteador.ir('/quadro/tv');
            document.documentElement.requestFullscreen?.().catch(() => undefined);
          }
        },
      },
      [sf(modoTv ? 'arrow-down-circle' : 'macwindow'), modoTv ? 'Sair da tela cheia' : 'Tela cheia'],
    ),
  ];
}

export { situacaoDaEtapa };
